// HTML body for "We Tested Every Agentic Coding Engine Against Every Local Model. The Results Were Weird."
// Single source of truth used by:
//   - BlogPost.js (dangerouslySetInnerHTML)
//   - scripts/prerender.mjs (noscript injection for SEO)
//
// Keep this in sync with sitemap.xml lastmod when content changes.

const html = `
<p>We expected the best model to win.</p>

<p>That's the intuition, right? Bigger parameter count. Better HumanEval score. Coder-tuned on more
data. You pick the strongest model you can fit in memory and everything downstream gets better.</p>

<p>Here's what we actually found: a 6-billion-parameter flash model beat a 32-billion-parameter
coder-tuned model on the same coding task — when each was paired with the right engine.</p>

<p>Model quality, as most people measure it, wasn't the variable that mattered.</p>

<h2>The lab</h2>

<p>At QCoda we run structured experiments before we ship anything — our version of a structured lab. Each lab has
a hypothesis, a test plan, and a graduation criterion. LAB-009 asked a specific question: when you
pair different agentic coding engines with different local models, what actually determines whether
the task succeeds?</p>

<p>We tested five engines: aider (in two modes — whole-file and diff), goose, opencode, pi, and
claude-code. Seven local models: qwen2.5-coder:32b, qwen3-coder:30b, gpt-oss:20b,
devstral-small-2:24b, glm-4.7-flash, gemma4:26b, gemma4:12b. Ten trials per engine-model pair.
Same task each time: add four specific routes to an existing Flask application. Same context — the
real spec files and project ledger passed in, the way QCoda actually runs in production.</p>

<p>Pass means all four routes present, file parses, no existing code dropped. Fail means anything
else. The pass-rate is what it is.</p>

<p>Here's the matrix:</p>

<div style="overflow-x:auto;margin:2rem 0">
<table style="width:100%;border-collapse:collapse;font-size:0.9rem;font-family:monospace">
  <thead>
    <tr style="border-bottom:2px solid #444">
      <th style="text-align:left;padding:0.5rem 1rem 0.5rem 0">Model</th>
      <th style="text-align:center;padding:0.5rem 0.75rem">aider:diff</th>
      <th style="text-align:center;padding:0.5rem 0.75rem">aider:whole</th>
      <th style="text-align:center;padding:0.5rem 0.75rem">goose</th>
      <th style="text-align:center;padding:0.5rem 0.75rem">claude-code</th>
      <th style="text-align:center;padding:0.5rem 0.75rem">pi</th>
    </tr>
  </thead>
  <tbody>
    <tr style="border-bottom:1px solid #333">
      <td style="padding:0.5rem 1rem 0.5rem 0">qwen2.5-coder:32b</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80;font-weight:bold">10/10 <span style="font-size:0.75em;color:#aaa">(21s)</span></td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">10/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#f87171">0/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#f87171">0/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#fbbf24">5/10</td>
    </tr>
    <tr style="border-bottom:1px solid #333">
      <td style="padding:0.5rem 1rem 0.5rem 0">qwen3-coder:30b</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80;font-weight:bold">10/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#f87171">0/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">10/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">10/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">9/10</td>
    </tr>
    <tr style="border-bottom:1px solid #333">
      <td style="padding:0.5rem 1rem 0.5rem 0">gpt-oss:20b</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80;font-weight:bold">10/10 <span style="font-size:0.75em;color:#aaa">(59s)</span></td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">9/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#fbbf24">5/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">9/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">9/10</td>
    </tr>
    <tr style="border-bottom:1px solid #333">
      <td style="padding:0.5rem 1rem 0.5rem 0">glm-4.7-flash</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">8/10 <span style="font-size:0.75em;color:#aaa">(474s)</span></td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#fbbf24">6/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#fbbf24">7/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">8/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80;font-weight:bold">10/10 <span style="font-size:0.75em;color:#aaa">(24s)</span></td>
    </tr>
    <tr>
      <td style="padding:0.5rem 1rem 0.5rem 0">devstral-small-2:24b</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80;font-weight:bold">10/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#888">—</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#f87171">1/10</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#888">—</td>
      <td style="text-align:center;padding:0.5rem 0.75rem;color:#4ade80">10/10</td>
    </tr>
  </tbody>
</table>
</div>

<p>The columns don't tell a consistent story by model. The rows don't tell a consistent story by
engine. The interactions are the finding.</p>

<h2>The edit-format revelation</h2>

<p>The biggest result in the matrix isn't about which engine is best. It's about why aider's two
modes produced opposite outcomes on the same model.</p>

<p>aider:whole went 0/10 on qwen3-coder:30b. aider:diff went 10/10 on the same model. Same model,
same context, same task. The only difference was the format aider used to request the edit.</p>

<p>Whole-file mode works like this: aider hands the model the existing file and says "here's what's
in it, give me back the complete new version." Under real context load — actual spec files, actual
project ledger, the stuff QCoda feeds into every production run — a 30B model can't hold all of
that <em>and</em> re-emit a 195-line file. It drops the task. It applies zero edits. It exits
cleanly. No error.</p>

<p>That's not a model bug. That's asking too much of the format.</p>

<p>Diff mode (SEARCH/REPLACE) works differently: aider says "find this exact block, replace it with
this." The model emits only the changed hunk. It never re-emits the file body — so it physically
cannot drop existing code. Under any context load, on every model we tested, aider:diff produced
zero truncation events across 18 cross-complexity runs.</p>

<p>Zero.</p>

<p>That's not a statistical result. That's a structural property of the format.</p>

<p>Here's the part that matters: the "model is dropping my code" bug — the one where a file goes
from 195 lines to 10 and the agent reports success — is overwhelmingly an edit-format problem, not
a model problem. We saw this problem before on projects. We called it the gutting failure. We blamed models.
We added guardrails. The actual fix was changing one flag:
<code>--edit-format diff</code>.</p>

<h2>The GLM surprise</h2>

<p>Look at the glm-4.7-flash row in the matrix. It's a flash-class model — fast, small, not
coder-tuned in the way qwen2.5-coder is. If you're selecting models by benchmark score or parameter
count, glm doesn't make your shortlist for a serious coding task.</p>

<p>Pi went 10/10 on it at 24 seconds.</p>

<p>That's the fastest clean result in the whole matrix, on a model you'd expect to struggle.
Meanwhile, pi went 5/10 on qwen2.5-coder:32b — 32 billion coder-tuned parameters — with two
truncation events.</p>

<p>The reason connects directly back to Episode 1. Pi routes through a structured tool-call
interface. glm-4.7-flash emits structured <code>tool_calls</code> natively — no normalization
needed, clean protocol compliance out of the box. qwen2.5-coder:32b, behind raw Ollama, emits
JSON-in-content. Pi parses the interface, finds no structured call, and works with degraded
information. Sometimes that's fine. Half the time it isn't.</p>

<p>That's not a size story. That's not a benchmark story. It's a protocol compliance story.</p>

<p>The model that wins on pi isn't the smartest model. It's the model that speaks pi's language.</p>

<h2>Heavy loops vs. low round-trips</h2>

<p>Claude-code was the most interesting engine to measure, because it revised its own prior verdict
during the lab.</p>

<p>The initial runs against devstral-small-2:24b with faithful context were not encouraging: 0/5. We had
logged a conclusion — "claude-code not viable on local OSS for real tasks." Then we ran it against
qwen3-coder:30b: 10/10. Against gpt-oss:20b: 9/10.</p>

<p>The prior "not viable" conclusion was an artifact of testing it only on models that couldn't
support its loop. Claude-code's multi-round agentic pattern — Read → Plan → Edit → Verify — burns
through a lot of turns. On a capable 30B model, those turns complete and the edits land. On a
weaker or protocol-incompatible model, the loop spends its budget reading and planning, and the
headless session ends before any edit is applied.</p>

<p>That's the heavy-loop problem. And it affects opencode too, which timed out at 1200 seconds on
qwen3-coder:30b — not because opencode is broken, but because its read-edit-verify-lint cycle on a
local 30B model running on commodity hardware runs out of runway before it finishes. (Opencode's
0/10 elsewhere is an unresolved invocation issue, not a discipline result — we're not counting that
against it.)</p>

<p>On local hardware, round-trip count is a first-class constraint. Low-round-trip engines —
aider:diff, pi — win on practicality, not because they're more sophisticated. Aider:diff finishes
in 21 seconds on qwen2.5-coder:32b. That's not just faster. It's 57 times cheaper in GPU
wall-clock than a 1200-second timeout, for a better outcome.</p>

<h2>What this means in practice</h2>

<p>The question "which model is best for coding?" is underspecified to the point of uselessness.</p>

<p>Better question: which engine are you running? Does your model emit structured tool calls
natively? What's your context load? How many round-trips does your hardware support in a reasonable
timeout?</p>

<p>These are infrastructure questions, not model questions. And if you're choosing models without
having answered them, you're debugging the wrong layer.</p>

<p>At QCoda, the lab results directly fed a routing function — <code>_resolve_edit_format()</code>
— that picks the right aider mode per model capability automatically. The operator doesn't configure
this. The orchestration layer knows from the capability index which models get <code>diff</code> and
which get <code>whole</code>. It's one of the first expressions of a broader bet: the right tool
for the right task is an orchestration decision, not a manual one.</p>

<p>That bet gets more interesting when you add task complexity into the routing signal. Which is
Episode 3.</p>

<hr />

<p><em>QCoda Labs runs structured experiments to understand what actually works in local
AI-assisted software development. This series documents what we found in LAB-009 — engine and model
compatibility at scale. All runs: local Ollama endpoints, no cloud inference, N=10 per cell.</em></p>
`;

module.exports = html;
